import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTime } from "luxon";
import lodash from "lodash";
import { useAtom } from "jotai";
import { userAtom } from "../../../jotai/atoms";
import { DocketAPIError, getAPIClient } from "../../../apiClient";
import { infoLog, errorLog } from "../../../utils/logger";
import { getDateTime } from "../../../utils/data";
import {
  checkBoxValueMap,
  getSearchRadios,
  getLocalizedLegalSexChoices,
} from "../../../utils/formUtils";
import ResponsiveDatePicker from "../../../components/form/ResponsiveDatePicker";
import FormFieldset from "../../../components/form/FormFieldset";
import { ImageIcon } from "../../../components/ImageIcon";

interface ProfileInfoCardProps {
  onError: (message: string) => void;
}

export function ProfileInfoCard({ onError }: ProfileInfoCardProps) {
  const { t } = useTranslation();
  const [user, setUser] = useAtom(userAtom);
  const [dob, setDob] = useState<Date | null>(null);
  const [checkboxValue, setCheckboxValue] = useState(getLocalizedLegalSexChoices(t));
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const updateProfileSchema = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
  });

  const {
    register,
    formState: { isValid },
    handleSubmit,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(updateProfileSchema),
  });

  useEffect(() => {
    const newValue = checkBoxValueMap(
      checkboxValue,
      lodash.findIndex(checkboxValue, function (item) {
        return (
          item.value === user?.legal_sex || item.value === user?.legal_sex?.charAt(0).toUpperCase()
        );
      })
    );
    setCheckboxValue(newValue);
    if (user?.dob) {
      setDob(getDateTime(user.dob).toJSDate());
    }
  }, [user]);

  const checkboxHandler = (value: any, index: number) => {
    const newValue = checkBoxValueMap(checkboxValue, index);
    infoLog(`newValue ${JSON.stringify(newValue)}`);
    setCheckboxValue(newValue);
  };

  const ageCheckPassed = () => {
    if (dob && DateTime.now().minus({ years: 13 }) <= DateTime.fromJSDate(dob)) {
      return false;
    }
    return true;
  };

  const onSubmitProfile = async (data: any) => {
    if (!ageCheckPassed()) {
      errorLog({ message: t("settings.dob_error_message") } as Error);
      onError(t("settings.dob_error_message"));
      return;
    }

    setSaving(true);
    try {
      const sex = lodash.find(checkboxValue, ["checked", true]);
      const legalSex = sex?.value ?? "U";
      const params = {
        first_name: data.firstName,
        last_name: data.lastName,
        legal_sex: legalSex,
        dob: dob ? DateTime.fromJSDate(dob).toISO() : null,
        language: "en",
      };
      infoLog(`onSubmitProfile ${JSON.stringify(params)}`);
      await getAPIClient().updateUser(params);
      const userResults = await getAPIClient().getUser();
      if (userResults) {
        await setUser({
          ...userResults,
          dob: dob ? DateTime.fromJSDate(dob).toLocaleString() : null,
        });
      }
      setSaving(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 500);
    } catch (e: unknown) {
      setSaving(false);
      if (e instanceof DocketAPIError) {
        onError(e.message);
      }
    }
  };

  return (
    <div className="card bg-base-100 max-md:rounded-none">
      <div className="bg-secondary text-secondary-content px-4 py-3 rounded-t-[var(--radius-box)] max-md:rounded-none">
        <h2 className="text-xl font-bold">{t("settings.update_profile")}</h2>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit(onSubmitProfile)} className="flex flex-col gap-4">
          <div className="grid max-md:grid-cols-1 grid-cols-2 gap-2">
            <FormFieldset label={t("onboarding.email-address")}>
              <input
                className="input input-primary w-full"
                type="email"
                value={user?.email || ""}
                disabled
              />
              <p className="text-sm text-base-content/60 mt-1">
                Email changes are not supported. To use a different email, delete your account and
                create a new one.
              </p>
            </FormFieldset>
            <FormFieldset label={t("immunizations.search_my_dob_label")} required>
              <ResponsiveDatePicker
                key="dateOfBirth"
                selectedDate={dob}
                setSelectedDate={(d) => setDob(d)}
              />
            </FormFieldset>
          </div>

          <div className="grid max-md:grid-cols-1 grid-cols-2 gap-2">
            <FormFieldset label={t("immunizations.search_my_first_name_label")} required>
              <input
                data-testid="input-field-firstName"
                className="input input-primary w-full"
                type="text"
                placeholder={t("immunizations.search_my_first_name_label")}
                defaultValue={user?.first_name || ""}
                {...register("firstName")}
              />
            </FormFieldset>
            <FormFieldset label={t("immunizations.search_my_last_name_label")} required>
              <input
                data-testid="input-field-lastName"
                className="input input-primary w-full"
                type="text"
                placeholder={t("immunizations.search_my_last_name_label")}
                defaultValue={user?.last_name || ""}
                {...register("lastName")}
              />
            </FormFieldset>
          </div>

          {getSearchRadios(
            checkboxValue,
            t("immunizations.search_my_legal_sex_label"),
            checkboxHandler,
            "",
            "gender",
            t
          )}

          <button className="btn btn-primary w-fit" type="submit" disabled={!isValid || saving}>
            {saving ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : showSuccess ? (
              <ImageIcon src="/assets/images/Icon_Check.png" className="w-5 h-5" />
            ) : (
              t("settings.update_profile")
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ProfileInfoCard;
